import { useReducedMotion } from '@mantine/hooks';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    Color,
    MathUtils,
    Object3D,
    PlaneGeometry,
    ShaderMaterial,
    Vector2,
    Vector3,
    type Group,
    type InstancedMesh
} from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { getArticleId } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { getConnectorArticleId } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { getWorldUnitsPerPixel } from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

const MARKER_RENDER_ORDER = 10000;
// Shared violet palette; symbols differ: snap points = round pulsing targets, snap ranges = lines with diamond end caps
const MARKER_COLOR_HEX = '#7c3aed';
const GLOW_COLOR_HEX = '#c084fc';
// Captured snap point uses the same blue as the snap feedback ring
const ACTIVE_COLOR_HEX = PRIMARY_COLOR_ACCENT_HEX;
const ACTIVE_GLOW_COLOR_HEX = '#2f8fff';
const ACTIVE_FADE_SPEED = 18;
const ACTIVE_MATCH_RADIUS_PX = 6;
const SHAPE_CIRCLE = 0;
const SHAPE_DIAMOND = 1;

// Quad half-sizes in pixels; the visible core is a fraction of it, the rest is room for glow/ripples
const POINT_MARKER_SIZE_PX = 38;
const RANGE_CAP_SIZE_PX = 16;

const RANGE_GLOW_WIDTH_PX = 18;
const RANGE_OUTLINE_WIDTH_PX = 8;
const RANGE_CORE_WIDTH_PX = 4;
const RANGE_FLOW_WIDTH_PX = 2.5;
const RANGE_DASH_SIZE_PX = 6;
const RANGE_GAP_SIZE_PX = 22;
const RANGE_FLOW_SPEED_PX = 14;

const FADE_SPEED = 10;
const FADE_EPSILON = 0.005;
const INTRO_STAGGER_S = 0.35;
const HEARTBEAT_HZ = 0.75;
const BREATHE_HZ = 0.25;
const REDUCED_MOTION_SINCE_SHOW_S = 1000;

const noRaycast = () => {};
const scratchPoint = new Vector3();

const breathe = (time: number) => 0.5 + 0.5 * Math.sin(time * BREATHE_HZ * Math.PI * 2);

const markerVertexShader = /* glsl */ `
    uniform vec2 uResolution;
    uniform float uSizePx;
    uniform float uSinceShow;
    uniform float uStagger;
    uniform vec3 uActivePos;
    uniform float uActiveEps;
    varying vec2 vUv;
    varying float vPhase;
    varying float vIntro;
    varying float vIsActive;

    void main() {
        vec4 worldCenter = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        vec4 clipCenter = projectionMatrix * viewMatrix * worldCenter;
        clipCenter.xy += position.xy * uSizePx * 2.0 / uResolution * clipCenter.w;

        vPhase = fract(sin(dot(worldCenter.xyz, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        vIntro = clamp((uSinceShow - vPhase * uStagger) / 0.5, 0.0, 1.0);
        vIsActive = 1.0 - step(uActiveEps, distance(worldCenter.xyz, uActivePos));
        vUv = position.xy;
        gl_Position = clipCenter;
    }
`;

// Layered screen-space marker: glow -> ripples -> intro shockwave -> rotating arcs -> white outline -> pulsing core
const markerFragmentShader = /* glsl */ `
    #define TAU 6.28318530718

    uniform vec3 uColor;
    uniform vec3 uGlowColor;
    uniform float uOpacity;
    uniform float uTime;
    uniform float uRipple;
    uniform float uArcs;
    uniform float uCoreRadius;
    uniform float uShape;
    uniform vec3 uActiveColor;
    uniform vec3 uActiveGlowColor;
    uniform float uActive;
    varying vec2 vUv;
    varying float vPhase;
    varying float vIntro;
    varying float vIsActive;

    float easeOutBack(float x) {
        float c1 = 1.70158;
        float c3 = c1 + 1.0;
        float y = x - 1.0;
        return 1.0 + c3 * y * y * y + c1 * y * y;
    }

    float ring(float r, float radius, float width, float aa) {
        return 1.0 - smoothstep(width * 0.5 - aa, width * 0.5 + aa, abs(r - radius));
    }

    vec4 over(vec4 dst, vec3 color, float alpha) {
        float a = alpha + dst.a * (1.0 - alpha);
        vec3 rgb = (color * alpha + dst.rgb * dst.a * (1.0 - alpha)) / max(a, 1e-4);
        return vec4(rgb, a);
    }

    void main() {
        float rawR = length(vUv);
        float aa = max(fwidth(rawR), 1e-4);
        float pop = max(easeOutBack(vIntro), 1e-3);
        // Circle (L2) or diamond (L1) distance field for the core/outline shape
        float shapeR = mix(rawR, (abs(vUv.x) + abs(vUv.y)) * 0.8, uShape);
        float r = shapeR / pop;
        float aaS = max(fwidth(shapeR), 1e-4) / pop;
        float shown = smoothstep(0.0, 0.15, vIntro);

        // Heartbeat only on snap points (uRipple), range caps stay calm to read differently
        float beat = pow(max(0.0, sin((uTime * ${HEARTBEAT_HZ.toFixed(2)} - vPhase * 0.08) * TAU)), 12.0) * uRipple;

        float coreR = uCoreRadius * (1.0 + 0.25 * beat);
        float core = 1.0 - smoothstep(coreR - aaS, coreR + aaS, r);
        float outlineR = coreR + uCoreRadius * 0.5;
        float outline = 1.0 - smoothstep(outlineR - aaS, outlineR + aaS, r);

        float angle = atan(vUv.y, vUv.x) / TAU + 0.5;
        float direction = vPhase > 0.5 ? 1.0 : -1.0;
        float seg = fract(angle * 3.0 + uTime * 0.4 * direction + vPhase);
        float arcMask = smoothstep(0.0, 0.05, seg) * (1.0 - smoothstep(0.55, 0.6, seg));
        float arcs = ring(r, outlineR + uCoreRadius * 0.85, uCoreRadius * 0.32, aaS) * arcMask * uArcs;

        float ripples = 0.0;
        for (int i = 0; i < 2; i++) {
            float t = fract(uTime * 0.6 + vPhase + float(i) * 0.5);
            ripples += ring(rawR, mix(outlineR, 1.0, t), 0.06, aa) * pow(1.0 - t, 1.5);
        }
        ripples *= uRipple * shown;

        float shock = ring(rawR, mix(0.15, 1.0, vIntro), 0.12 * (1.0 - vIntro) + 0.02, aa) * (1.0 - vIntro) * shown;

        float glow = exp(-rawR * rawR * 7.0) * (0.35 + 0.45 * beat) * shown;

        vec3 arcColor = mix(uGlowColor, vec3(1.0), 0.35);
        vec3 coreColor = mix(uColor, uGlowColor, 0.25 + 0.6 * beat);

        vec4 acc = vec4(uGlowColor, glow);
        acc = over(acc, uGlowColor, clamp(ripples * 0.85, 0.0, 1.0));
        acc = over(acc, vec3(1.0), shock);
        acc = over(acc, arcColor, arcs * 0.95);
        acc = over(acc, vec3(1.0), outline);
        acc = over(acc, coreColor, core);

        // Captured snap point: the whole circle turns into a filled blue disc with white rim and blue glow
        float captured = vIsActive * uActive;
        if (captured > 0.0) {
            float discR = mix(outlineR, 0.46, easeOutBack(captured));
            float disc = 1.0 - smoothstep(discR - aaS, discR + aaS, r);
            float rim = ring(r, discR + 0.035, 0.06, aaS);
            float halo = exp(-rawR * rawR * 4.0) * 0.6;
            float t = fract(uTime * 1.2);
            float captureRipple = ring(rawR, mix(discR, 1.0, t), 0.05, aa) * pow(1.0 - t, 1.5) * uRipple;

            vec4 activeAcc = vec4(uActiveGlowColor, halo);
            activeAcc = over(activeAcc, uActiveGlowColor, captureRipple);
            activeAcc = over(activeAcc, vec3(1.0), rim);
            activeAcc = over(activeAcc, mix(uActiveColor, uActiveGlowColor, 0.2 * beat), disc);
            acc = mix(acc, activeAcc, captured);
        }

        float alpha = acc.a * uOpacity;
        if (alpha < 0.002) discard;

        gl_FragColor = vec4(acc.rgb, alpha);
        #include <colorspace_fragment>
    }
`;

type MarkerMaterialOptions = {
    sizePx: number;
    coreRadius: number;
    color: string;
    glowColor: string;
    shape: number;
    ripple: boolean;
    arcs: boolean;
};

const createMarkerMaterial = ({ sizePx, coreRadius, color, glowColor, shape, ripple, arcs }: MarkerMaterialOptions) =>
    new ShaderMaterial({
        uniforms: {
            uColor: { value: new Color(color) },
            uGlowColor: { value: new Color(glowColor) },
            uShape: { value: shape },
            uOpacity: { value: 0 },
            uTime: { value: 0 },
            uSinceShow: { value: 0 },
            uStagger: { value: INTRO_STAGGER_S },
            uRipple: { value: ripple ? 1 : 0 },
            uArcs: { value: arcs ? 1 : 0 },
            uCoreRadius: { value: coreRadius },
            uSizePx: { value: sizePx },
            uResolution: { value: new Vector2(1, 1) },
            uActivePos: { value: new Vector3() },
            // Tolerance (world units) for matching the captured snap point's instance center in the shader
            uActiveEps: { value: 1e-3 },
            uActive: { value: 0 },
            uActiveColor: { value: new Color(ACTIVE_COLOR_HEX) },
            uActiveGlowColor: { value: new Color(ACTIVE_GLOW_COLOR_HEX) }
        },
        vertexShader: markerVertexShader,
        fragmentShader: markerFragmentShader,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        toneMapped: false
    });

const createRangeMaterial = (color: string, linewidth: number, dashed = false) =>
    new LineMaterial({
        color: new Color(color).getHex(),
        linewidth,
        resolution: new Vector2(1, 1),
        depthTest: false,
        depthWrite: false,
        transparent: true,
        toneMapped: false,
        opacity: 0,
        dashed,
        dashSize: RANGE_DASH_SIZE_PX,
        gapSize: RANGE_GAP_SIZE_PX
    });

type RangeLayer = { material: LineMaterial; width: number; opacity: number; pulse: number };

export const ConnectorSnapPointsMarker = () => {
    const size = useThree((state) => state.size);
    const reducedMotion = useReducedMotion();
    const markerGroupRef = useRef<Group | null>(null);
    const rangeGroupRef = useRef<Group | null>(null);
    const pointMeshRef = useRef<InstancedMesh | null>(null);
    const capMeshRef = useRef<InstancedMesh | null>(null);
    const fadeRef = useRef(0);
    const wasVisibleRef = useRef(false);
    const showStartRef = useRef(0);
    const dashOffsetRef = useRef(0);
    const activeRef = useRef(0);
    const activeIndexRef = useRef(-1);

    const { snapPoints, snapRanges } = usePlacementStore(
        useShallow((state) => ({
            snapPoints: state.manualPlacementData.snapPoints,
            snapRanges: state.manualPlacementData.snapRanges
        }))
    );

    const markerObject = useMemo(() => new Object3D(), []);
    const quadGeometry = useMemo(() => new PlaneGeometry(2, 2), []);
    const pointMaterial = useMemo(
        () =>
            createMarkerMaterial({
                sizePx: POINT_MARKER_SIZE_PX,
                coreRadius: 0.17,
                color: MARKER_COLOR_HEX,
                glowColor: GLOW_COLOR_HEX,
                shape: SHAPE_CIRCLE,
                ripple: true,
                arcs: true
            }),
        []
    );
    const capMaterial = useMemo(
        () =>
            createMarkerMaterial({
                sizePx: RANGE_CAP_SIZE_PX,
                coreRadius: 0.24,
                color: MARKER_COLOR_HEX,
                glowColor: GLOW_COLOR_HEX,
                shape: SHAPE_DIAMOND,
                ripple: false,
                arcs: false
            }),
        []
    );
    const rangeLayers = useMemo<RangeLayer[]>(
        () => [
            { material: createRangeMaterial(GLOW_COLOR_HEX, RANGE_GLOW_WIDTH_PX), width: RANGE_GLOW_WIDTH_PX, opacity: 0.22, pulse: 0.3 },
            { material: createRangeMaterial('#ffffff', RANGE_OUTLINE_WIDTH_PX), width: RANGE_OUTLINE_WIDTH_PX, opacity: 0.9, pulse: 0 },
            { material: createRangeMaterial(MARKER_COLOR_HEX, RANGE_CORE_WIDTH_PX), width: RANGE_CORE_WIDTH_PX, opacity: 1, pulse: 0 },
            { material: createRangeMaterial('#ffffff', RANGE_FLOW_WIDTH_PX, true), width: RANGE_FLOW_WIDTH_PX, opacity: 0.7, pulse: 0 }
        ],
        []
    );

    useFrame(({ camera, clock, invalidate, size: viewport }, delta) => {
        const group = markerGroupRef.current;
        if (!group) {
            return;
        }

        const state = usePlacementStore.getState();
        const isDragActive =
            state.isDragNewArticleStartRef.current ||
            state.isDragNewArticleActiveRef.current ||
            state.isAutoDragActiveRef.current ||
            state.isDragActiveRef.current;

        let targetVisible = false;
        if (isDragActive) {
            // Show markers only if connector/anchor articles are being dragged
            const dragAnchorId = Object.keys(state.articleInstanceSelections)[0];
            const draggedArticleId =
                getConnectorArticleId(state, dragAnchorId) ??
                (dragAnchorId ? getArticleId(dragAnchorId, state) : undefined);

            targetVisible =
                !state.manualPlacementData.isLoading &&
                draggedArticleId !== null &&
                state.manualPlacementData.articleId === draggedArticleId &&
                state.manualPlacementData.candidates.length > 0 &&
                (state.manualPlacementData.settingType === SettingModelSettingType.Connectors ||
                    state.manualPlacementData.settingType === SettingModelSettingType.Anchors);
        }

        const elapsed = clock.getElapsedTime();
        if (targetVisible && !wasVisibleRef.current) {
            showStartRef.current = elapsed;
        }
        wasVisibleRef.current = targetVisible;

        const targetFade = targetVisible ? 1 : 0;
        let fade = reducedMotion ? targetFade : MathUtils.damp(fadeRef.current, targetFade, FADE_SPEED, delta);
        if (Math.abs(fade - targetFade) < FADE_EPSILON) {
            fade = targetFade;
        }
        fadeRef.current = fade;

        group.visible = fade > 0;
        if (!group.visible) {
            return;
        }

        const time = reducedMotion ? 0 : elapsed;
        const sinceShow = reducedMotion ? REDUCED_MOTION_SINCE_SHOW_S : elapsed - showStartRef.current;
        const breath = breathe(time);
        // Ease-out-back so the ranges pop in alongside the point markers
        const introT = MathUtils.clamp(sinceShow / 0.5, 0, 1);
        const pop = 1 + 2.70158 * (introT - 1) ** 3 + 1.70158 * (introT - 1) ** 2;

        for (const mesh of [pointMeshRef.current, capMeshRef.current]) {
            if (!mesh) {
                continue;
            }
            const { uniforms } = mesh.material as ShaderMaterial;
            uniforms.uOpacity.value = fade;
            uniforms.uTime.value = time;
            uniforms.uSinceShow.value = sinceShow;
            if (mesh === pointMeshRef.current) {
                uniforms.uRipple.value = reducedMotion ? 0 : 1;
            }
        }

        const pointMesh = pointMeshRef.current;
        let isActiveAnimating = false;
        if (pointMesh) {
            const { uniforms } = pointMesh.material as ShaderMaterial;
            // Find the snap point the dragged object is currently captured by
            const snapTarget = state.snapPointMarkerRef.current;
            let activeIndex = -1;
            if (snapTarget?.visible) {
                const matchRadius =
                    ACTIVE_MATCH_RADIUS_PX * getWorldUnitsPerPixel(camera, snapTarget.position, viewport.height);
                let bestDistanceSq = matchRadius * matchRadius;
                const { snapPoints: currentSnapPoints } = state.manualPlacementData;
                for (let index = 0; index < currentSnapPoints.length; index++) {
                    const { position } = currentSnapPoints[index].placement;
                    const distanceSq = snapTarget.position.distanceToSquared(
                        scratchPoint.set(position.x, position.y, position.z)
                    );
                    if (distanceSq <= bestDistanceSq) {
                        bestDistanceSq = distanceSq;
                        activeIndex = index;
                    }
                }
            }

            if (activeIndex !== activeIndexRef.current) {
                if (activeIndex >= 0) {
                    const { position } = state.manualPlacementData.snapPoints[activeIndex].placement;
                    uniforms.uActivePos.value.set(position.x, position.y, position.z);
                    activeRef.current = 0;
                }
                activeIndexRef.current = activeIndex;
            }

            const targetActive = activeIndex >= 0 ? 1 : 0;
            let active = reducedMotion
                ? targetActive
                : MathUtils.damp(activeRef.current, targetActive, ACTIVE_FADE_SPEED, delta);
            if (Math.abs(active - targetActive) < FADE_EPSILON) {
                active = targetActive;
            }
            activeRef.current = active;
            uniforms.uActive.value = active;
            isActiveAnimating = active !== targetActive;
        }

        const rangeGroup = rangeGroupRef.current;
        if (rangeGroup && snapRanges.length > 0) {
            const { startPoint, endPoint } = snapRanges[0];
            scratchPoint.set(
                (startPoint.x + endPoint.x) / 2,
                (startPoint.y + endPoint.y) / 2,
                (startPoint.z + endPoint.z) / 2
            );
            // Keep dashes a constant on-screen length independent of zoom
            const dashScale = 1 / Math.max(getWorldUnitsPerPixel(camera, scratchPoint, viewport.height), 1e-6);
            if (!reducedMotion) {
                dashOffsetRef.current = (dashOffsetRef.current - delta * RANGE_FLOW_SPEED_PX) % (RANGE_DASH_SIZE_PX + RANGE_GAP_SIZE_PX);
            }

            rangeGroup.children.forEach((child) => {
                const layer = child.userData.layer as RangeLayer | undefined;
                if (!layer) {
                    return;
                }
                layer.material.opacity = fade * layer.opacity * (1 + layer.pulse * (breath * 2 - 0.5));
                layer.material.linewidth = layer.width * Math.max(pop, 0) * (1 + layer.pulse * breath);
                if (layer.material.dashed) {
                    layer.material.dashScale = dashScale;
                    layer.material.dashOffset = dashOffsetRef.current;
                }
            });
        }

        // frameloop is "demand": keep ticking while animating
        if (fade !== targetFade || isActiveAnimating || !reducedMotion) {
            invalidate();
        }
    });

    useLayoutEffect(() => {
        const pointMesh = pointMeshRef.current;
        if (!pointMesh) {
            return;
        }

        snapPoints.forEach(({ placement }, index) => {
            markerObject.position.set(placement.position.x, placement.position.y, placement.position.z);
            markerObject.updateMatrix();
            pointMesh.setMatrixAt(index, markerObject.matrix);
        });
        pointMesh.instanceMatrix.needsUpdate = true;
    }, [markerObject, snapPoints]);

    useLayoutEffect(() => {
        const capMesh = capMeshRef.current;
        if (!capMesh) {
            return;
        }

        snapRanges.forEach(({ startPoint, endPoint }, index) => {
            markerObject.position.set(startPoint.x, startPoint.y, startPoint.z);
            markerObject.updateMatrix();
            capMesh.setMatrixAt(index * 2, markerObject.matrix);
            markerObject.position.set(endPoint.x, endPoint.y, endPoint.z);
            markerObject.updateMatrix();
            capMesh.setMatrixAt(index * 2 + 1, markerObject.matrix);
        });
        capMesh.instanceMatrix.needsUpdate = true;
    }, [markerObject, snapRanges]);

    const rangeGeometry = useMemo(() => {
        const geometry = new LineSegmentsGeometry();
        if (snapRanges.length > 0) {
            const positions = new Float32Array(snapRanges.length * 6);
            snapRanges.forEach(({ startPoint, endPoint }, index) => {
                positions.set([startPoint.x, startPoint.y, startPoint.z, endPoint.x, endPoint.y, endPoint.z], index * 6);
            });
            geometry.setPositions(positions);
        }
        return geometry;
    }, [snapRanges]);

    const rangeLines = useMemo(
        () =>
            rangeLayers.map((layer, index) => {
                const lines = new LineSegments2(rangeGeometry, layer.material);
                // Layers stack glow -> outline -> core -> flow, all below the point markers
                lines.renderOrder = MARKER_RENDER_ORDER - rangeLayers.length + index;
                lines.frustumCulled = false;
                lines.raycast = noRaycast;
                lines.userData.layer = layer;
                if (layer.material.dashed && snapRanges.length > 0) {
                    lines.computeLineDistances();
                }
                return lines;
            }),
        [rangeGeometry, rangeLayers, snapRanges.length]
    );

    useLayoutEffect(() => {
        rangeLayers.forEach(({ material }) => material.resolution.set(size.width, size.height));
        pointMaterial.uniforms.uResolution.value.set(size.width, size.height);
        capMaterial.uniforms.uResolution.value.set(size.width, size.height);
    }, [capMaterial, pointMaterial, rangeLayers, size]);

    useEffect(() => () => rangeGeometry.dispose(), [rangeGeometry]);
    useEffect(
        () => () => {
            quadGeometry.dispose();
            pointMaterial.dispose();
            capMaterial.dispose();
            rangeLayers.forEach(({ material }) => material.dispose());
        },
        [capMaterial, pointMaterial, quadGeometry, rangeLayers]
    );

    return (
        <group ref={markerGroupRef} visible={false}>
            {snapRanges.length > 0 && (
                <>
                    <group ref={rangeGroupRef}>
                        {rangeLines.map((lines) => (
                            <primitive key={lines.uuid} object={lines} />
                        ))}
                    </group>
                    <instancedMesh
                        ref={capMeshRef}
                        args={[quadGeometry, capMaterial, snapRanges.length * 2]}
                        count={snapRanges.length * 2}
                        frustumCulled={false}
                        raycast={noRaycast}
                        renderOrder={MARKER_RENDER_ORDER}
                    />
                </>
            )}
            <instancedMesh
                ref={pointMeshRef}
                args={[quadGeometry, pointMaterial, snapPoints.length]}
                count={snapPoints.length}
                frustumCulled={false}
                raycast={noRaycast}
                renderOrder={MARKER_RENDER_ORDER}
            />
        </group>
    );
};
